import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import { getSEOConfig, updateSEOTags } from '../../i18n/utils/seo';
import type { SupportedLanguage } from '../../i18n/utils/language';

export default function JavaScriptTimestamp() {
  const { t, i18n } = useTranslation(['programming', 'common']);
  const [copySuccess, setCopySuccess] = useState('');

  // Update SEO
  useEffect(() => {
    const currentLang = i18n.language as SupportedLanguage;
    const seoConfig = getSEOConfig('javascriptTimestamp', currentLang);
    updateSEOTags(seoConfig, currentLang);
    document.documentElement.lang = currentLang;
  }, [i18n.language]);

  // Copy to clipboard
  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(id);
      setTimeout(() => setCopySuccess(''), 2000);
    });
  };

  const CodeBlock = ({ code, language = 'javascript', id }: { code: string; language?: string; id: string }) => (
    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative group">
      <button
        onClick={() => copyToClipboard(code, id)}
        className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs transition-colors opacity-0 group-hover:opacity-100"
      >
        {copySuccess === id ? t('common:button.copied') : t('common:button.copy')}
      </button>
      <pre className="text-green-400 text-sm font-mono whitespace-pre overflow-x-auto">
        {code}
      </pre>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-5xl mx-auto px-4 py-4 md:py-8">
        {/* Page Header */}
        <div className="text-center mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            ⚡ {t('javascript.mainTitleSEO')}
          </h1>
          <p className="text-lg text-gray-600">{t('javascript.subtitle')}</p>
        </div>

        {/* Introduction */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <p className="text-gray-700 leading-relaxed">{t('javascript.intro')}</p>
        </div>

        {/* Get Current Timestamp */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Get Current Unix Timestamp in JavaScript</h2>
          <p className="text-gray-600 mb-6">
            Learn how to get the current Unix timestamp in JavaScript. JavaScript returns timestamps in milliseconds by default, but you can easily convert to seconds (Unix timestamp).
          </p>

          <div className="space-y-6">
            {/* Date.now() */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">1</span>
                {t('javascript.dateNow')}
              </h3>
              <CodeBlock
                id="js-1"
                code={`// Get current timestamp in milliseconds
const timestamp = Date.now();
console.log(timestamp);  // Output: 1735948800123

// Convert to seconds (Unix timestamp)
const unixTimestamp = Math.floor(Date.now() / 1000);
console.log(unixTimestamp);  // Output: 1735948800

// ⚡ Most efficient method - no object creation`}
              />
              <p className="mt-2 text-sm text-gray-600">💡 Date.now() is the fastest and most recommended method</p>
            </div>

            {/* getTime() */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">2</span>
                {t('javascript.getTime')}
              </h3>
              <CodeBlock
                id="js-2"
                code={`// Using getTime() method
const timestamp = new Date().getTime();
console.log(timestamp);  // Output: 1735948800123

// Same as Date.now() but creates Date object
// Slightly slower than Date.now()`}
              />
            </div>

            {/* valueOf() */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">3</span>
                {t('javascript.valueOf')}
              </h3>
              <CodeBlock
                id="js-3"
                code={`// Using valueOf() method
const timestamp = new Date().valueOf();
console.log(timestamp);  // Output: 1735948800123

// Identical to getTime()
console.log(new Date().getTime() === new Date().valueOf());  // true`}
              />
            </div>

            {/* Unary Plus */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">4</span>
                {t('javascript.unaryPlus')}
              </h3>
              <CodeBlock
                id="js-4"
                code={`// Using unary + operator (shorthand)
const timestamp = +new Date();
console.log(timestamp);  // Output: 1735948800123

// Compact but less readable
// Not recommended for production code`}
              />
            </div>
          </div>
        </div>

        {/* Convert Timestamp to Date */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('javascript.toDateTitle')}</h2>
          <p className="text-gray-600 mb-6">{t('javascript.toDateDesc')}</p>

          <div className="space-y-6">
            {/* Create Date */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">1</span>
                {t('javascript.newDate')}
              </h3>
              <CodeBlock
                id="js-5"
                code={`// Create Date from timestamp (milliseconds)
const timestamp = 1735948800123;
const date = new Date(timestamp);
console.log(date);  // Output: 2025-01-04T04:00:00.123Z

// From Unix timestamp (seconds) - convert to milliseconds
const unixTimestamp = 1735948800;
const date2 = new Date(unixTimestamp * 1000);
console.log(date2);  // Output: 2025-01-04T04:00:00.000Z

// Get individual components
console.log(date2.getFullYear());   // 2025
console.log(date2.getMonth());      // 0 (January is 0)
console.log(date2.getDate());       // 4
console.log(date2.getHours());      // Hour (local timezone)
console.log(date2.getMinutes());    // Minute
console.log(date2.getSeconds());    // Second`}
              />
            </div>

            {/* Format Date */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">2</span>
                {t('javascript.formatDate')}
              </h3>
              <CodeBlock
                id="js-6"
                code={`const timestamp = 1735948800000;
const date = new Date(timestamp);

// ISO 8601 format
console.log(date.toISOString());
// Output: 2025-01-04T04:00:00.000Z

// Date string
console.log(date.toDateString());
// Output: Sat Jan 04 2025

// Time string
console.log(date.toTimeString());
// Output: 12:00:00 GMT+0800 (China Standard Time)

// UTC string
console.log(date.toUTCString());
// Output: Sat, 04 Jan 2025 04:00:00 GMT

// Manual formatting
const formatted = \`\${date.getFullYear()}-\${String(date.getMonth() + 1).padStart(2, '0')}-\${String(date.getDate()).padStart(2, '0')}\`;
console.log(formatted);  // 2025-01-04`}
              />
            </div>

            {/* toLocaleString */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">3</span>
                {t('javascript.toLocaleString')}
              </h3>
              <CodeBlock
                id="js-7"
                code={`const timestamp = 1735948800000;
const date = new Date(timestamp);

// Default locale
console.log(date.toLocaleString());
// Output: 1/4/2025, 12:00:00 PM (depends on browser locale)

// Specific locale
console.log(date.toLocaleString('en-US'));
// Output: 1/4/2025, 12:00:00 PM

console.log(date.toLocaleString('zh-CN'));
// Output: 2025/1/4 12:00:00

// Custom formatting with options
const options = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false
};

console.log(date.toLocaleString('en-US', options));
// Output: January 4, 2025, 12:00:00

console.log(date.toLocaleString('zh-CN', options));
// Output: 2025年1月4日 12:00:00

// Different timezones
console.log(date.toLocaleString('en-US', { timeZone: 'America/New_York' }));
console.log(date.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));`}
              />
            </div>
          </div>
        </div>

        {/* Convert Date to Timestamp */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('javascript.toTimestampTitle')}</h2>
          <p className="text-gray-600 mb-6">{t('javascript.toTimestampDesc')}</p>

          <div className="space-y-6">
            {/* Parse Date String */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded text-sm mr-3">1</span>
                {t('javascript.parseDate')}
              </h3>
              <CodeBlock
                id="js-8"
                code={`// Parse ISO 8601 string
const isoString = '2025-01-04T12:00:00';
const timestamp1 = new Date(isoString).getTime();
console.log(timestamp1);

// Parse date string (various formats)
const dateStrings = [
  '2025-01-04',
  '01/04/2025',
  'Jan 4, 2025',
  '2025-01-04T12:00:00Z',
  '2025-01-04T12:00:00+08:00'
];

dateStrings.forEach(str => {
  const timestamp = new Date(str).getTime();
  console.log(\`\${str} -> \${timestamp}\`);
});

// Using Date.parse() (returns milliseconds)
const timestamp2 = Date.parse('2025-01-04T12:00:00');
console.log(timestamp2);  // Same as new Date().getTime()

// ⚠️ Warning: Date parsing without explicit format can be inconsistent
// across browsers. Use explicit ISO 8601 format or libraries`}
              />
            </div>

            {/* Create from Components */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded text-sm mr-3">2</span>
                {t('javascript.dateComponents')}
              </h3>
              <CodeBlock
                id="js-9"
                code={`// Create date from components (local timezone)
const date = new Date(2025, 0, 4, 12, 0, 0);  // Month is 0-indexed
const timestamp = date.getTime();
console.log(timestamp);

// Individual parameters:
// new Date(year, monthIndex, day, hours, minutes, seconds, milliseconds)
const date2 = new Date(2025, 0, 4, 12, 30, 45, 500);
console.log(date2.getTime());

// Create UTC date
const utcDate = new Date(Date.UTC(2025, 0, 4, 12, 0, 0));
const utcTimestamp = utcDate.getTime();
console.log(utcTimestamp);

// Get Unix timestamp (seconds)
const unixTimestamp = Math.floor(date.getTime() / 1000);
console.log(unixTimestamp);`}
              />
            </div>
          </div>
        </div>

        {/* Popular Libraries */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('javascript.librariesTitle')}</h2>

          <div className="space-y-6">
            {/* Day.js */}
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-gray-800 mb-3">
                {t('javascript.dayJs')} <span className="text-sm text-green-600">✓ Recommended</span>
              </h3>
              <CodeBlock
                id="js-10"
                code={`// Install: npm install dayjs
import dayjs from 'dayjs';

// Get current timestamp
const timestamp = dayjs().unix();  // Unix timestamp (seconds)
const timestampMs = dayjs().valueOf();  // Milliseconds

// From timestamp
const date = dayjs.unix(1735948800);
console.log(date.format('YYYY-MM-DD HH:mm:ss'));

// Timezone support (with plugin)
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);

const ny = dayjs().tz('America/New_York').format();
const tokyo = dayjs().tz('Asia/Tokyo').format();

// Formatting
console.log(dayjs().format('YYYY-MM-DD'));
console.log(dayjs().format('MMM D, YYYY h:mm A'));

// Manipulation
const tomorrow = dayjs().add(1, 'day');
const lastWeek = dayjs().subtract(7, 'day');`}
              />
            </div>

            {/* date-fns */}
            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="font-semibold text-gray-800 mb-3">
                {t('javascript.dateFns')}
              </h3>
              <CodeBlock
                id="js-11"
                code={`// Install: npm install date-fns
import { format, parseISO, getUnixTime, fromUnixTime } from 'date-fns';

// Get Unix timestamp
const timestamp = getUnixTime(new Date());
console.log(timestamp);  // Unix timestamp (seconds)

// From timestamp
const date = fromUnixTime(1735948800);
console.log(date);

// Format date
const formatted = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
console.log(formatted);

// Parse ISO string
const parsed = parseISO('2025-01-04T12:00:00');
console.log(getUnixTime(parsed));

// Timezone support (with date-fns-tz)
import { formatInTimeZone } from 'date-fns-tz';
const nyTime = formatInTimeZone(
  new Date(),
  'America/New_York',
  'yyyy-MM-dd HH:mm:ss zzz'
);`}
              />
            </div>

            {/* Luxon */}
            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="font-semibold text-gray-800 mb-3">
                {t('javascript.luxon')}
              </h3>
              <CodeBlock
                id="js-12"
                code={`// Install: npm install luxon
import { DateTime } from 'luxon';

// Get current timestamp
const timestamp = DateTime.now().toUnixInteger();  // Seconds
const timestampMs = DateTime.now().toMillis();  // Milliseconds

// From timestamp
const dt = DateTime.fromSeconds(1735948800);
console.log(dt.toISO());

// Timezone handling (built-in)
const nyTime = DateTime.now().setZone('America/New_York');
console.log(nyTime.toFormat('yyyy-MM-dd HH:mm:ss ZZZZ'));

const tokyo = DateTime.now().setZone('Asia/Tokyo');
console.log(tokyo.toISO());

// Formatting
console.log(dt.toFormat('yyyy-MM-dd'));
console.log(dt.toFormat('LLL dd, yyyy'));

// Manipulation
const tomorrow = DateTime.now().plus({ days: 1 });
const nextWeek = DateTime.now().plus({ weeks: 1 });`}
              />
            </div>
          </div>
        </div>

        {/* Common Patterns */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Common JavaScript Timestamp Patterns</h2>

          <div className="space-y-6">
            {/* Check if date is today */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">Check if timestamp is today</h3>
              <CodeBlock
                id="js-13"
                code={`function isToday(timestamp) {
  const date = new Date(timestamp);
  const today = new Date();

  return date.getDate() === today.getDate() &&
         date.getMonth() === today.getMonth() &&
         date.getFullYear() === today.getFullYear();
}

console.log(isToday(Date.now()));  // true
console.log(isToday(Date.now() - 86400000));  // false (yesterday)`}
              />
            </div>

            {/* Time difference */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">Calculate time difference</h3>
              <CodeBlock
                id="js-14"
                code={`function getTimeDifference(timestamp1, timestamp2) {
  const diff = Math.abs(timestamp2 - timestamp1);

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  return { days, hours: hours % 24, minutes: minutes % 60, seconds: seconds % 60 };
}

const ts1 = Date.now();
const ts2 = ts1 + (2 * 24 * 60 * 60 * 1000);  // 2 days later
console.log(getTimeDifference(ts1, ts2));
// Output: { days: 2, hours: 0, minutes: 0, seconds: 0 }`}
              />
            </div>

            {/* Relative time */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">Display relative time (e.g., "2 hours ago")</h3>
              <CodeBlock
                id="js-15"
                code={`function getRelativeTime(timestamp) {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);

  if (seconds < 60) return \`\${seconds} seconds ago\`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return \`\${minutes} minute\${minutes > 1 ? 's' : ''} ago\`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return \`\${hours} hour\${hours > 1 ? 's' : ''} ago\`;

  const days = Math.floor(hours / 24);
  if (days < 7) return \`\${days} day\${days > 1 ? 's' : ''} ago\`;

  const weeks = Math.floor(days / 7);
  return \`\${weeks} week\${weeks > 1 ? 's' : ''} ago\`;
}

console.log(getRelativeTime(Date.now() - 3600000));  // "1 hour ago"
console.log(getRelativeTime(Date.now() - 86400000 * 3));  // "3 days ago"`}
              />
            </div>
          </div>
        </div>

        {/* Format Timestamp as HH:MM:SS */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Format JavaScript Timestamp as HH:MM:SS</h2>
          <p className="text-gray-600 mb-6">
            Learn how to format timestamps as time strings in HH:MM:SS format (hour:minute:second) using JavaScript.
          </p>

          <div className="space-y-6">
            {/* Basic HH:MM:SS */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">1</span>
                24-Hour Format (HH:MM:SS)
              </h3>
              <CodeBlock
                id="js-hhmm-1"
                code={`function formatTimestampHHMMSS(timestamp) {
  const date = new Date(timestamp);

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return \`\${hours}:\${minutes}:\${seconds}\`;
}

const timestamp = 1735948800000;
console.log(formatTimestampHHMMSS(timestamp));
// Output: "12:00:00"

// Current time in HH:MM:SS
console.log(formatTimestampHHMMSS(Date.now()));
// Output: "14:35:22" (example)`}
              />
            </div>

            {/* 12-Hour Format */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">2</span>
                12-Hour Format with AM/PM
              </h3>
              <CodeBlock
                id="js-hhmm-2"
                code={`function formatTimestamp12Hour(timestamp) {
  const date = new Date(timestamp);

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;  // Convert 0 to 12 for midnight
  hours = String(hours).padStart(2, '0');

  return \`\${hours}:\${minutes}:\${seconds} \${ampm}\`;
}

const timestamp = 1735948800000;
console.log(formatTimestamp12Hour(timestamp));
// Output: "12:00:00 PM"

console.log(formatTimestamp12Hour(Date.now()));
// Output: "02:35:22 PM" (example)`}
              />
            </div>

            {/* Using toLocaleTimeString */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded text-sm mr-3">3</span>
                Using toLocaleTimeString() Method
              </h3>
              <CodeBlock
                id="js-hhmm-3"
                code={`const timestamp = 1735948800000;
const date = new Date(timestamp);

// Default locale, 24-hour format
console.log(date.toLocaleTimeString('en-US', { hour12: false }));
// Output: "12:00:00"

// 12-hour format with AM/PM
console.log(date.toLocaleTimeString('en-US', { hour12: true }));
// Output: "12:00:00 PM"

// Custom options
console.log(date.toLocaleTimeString('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false
}));
// Output: "12:00:00"

// Different locales
console.log(date.toLocaleTimeString('zh-CN'));
// Output: "12:00:00" (Chinese format)`}
              />
            </div>

            {/* HH:MM (without seconds) */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded text-sm mr-3">4</span>
                Format as HH:MM (No Seconds)
              </h3>
              <CodeBlock
                id="js-hhmm-4"
                code={`function formatHHMM(timestamp) {
  const date = new Date(timestamp);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return \`\${hours}:\${minutes}\`;
}

console.log(formatHHMM(Date.now()));
// Output: "14:35"

// Using toLocaleTimeString
const date = new Date();
console.log(date.toLocaleTimeString('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false
}));
// Output: "14:35"`}
              />
            </div>

            {/* Milliseconds precision */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded text-sm mr-3">5</span>
                With Milliseconds (HH:MM:SS.mmm)
              </h3>
              <CodeBlock
                id="js-hhmm-5"
                code={`function formatWithMilliseconds(timestamp) {
  const date = new Date(timestamp);

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

  return \`\${hours}:\${minutes}:\${seconds}.\${milliseconds}\`;
}

console.log(formatWithMilliseconds(Date.now()));
// Output: "14:35:22.458"`}
              />
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8 border-2 border-yellow-200">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            ⚡ {t('common.bestPractices')}
          </h2>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Use <code className="bg-gray-100 px-1.5 py-0.5 rounded">Date.now()</code> for getting current timestamp - it's faster than <code className="bg-gray-100 px-1.5 py-0.5 rounded">new Date().getTime()</code></span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Always store timestamps in UTC (milliseconds or seconds) in databases</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Use ISO 8601 format for date strings (<code className="bg-gray-100 px-1.5 py-0.5 rounded">toISOString()</code>) for consistency</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Use modern libraries like Day.js or date-fns instead of Moment.js (deprecated)</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Be aware that JavaScript Date months are 0-indexed (January = 0)</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Use <code className="bg-gray-100 px-1.5 py-0.5 rounded">toLocaleString()</code> for user-friendly date formatting</span>
            </li>
          </ul>
        </div>

        {/* Related Tools */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {t('common:relatedTools', { defaultValue: 'Related Tools' })}
          </h2>

          <div className="grid md:grid-cols-3 gap-4">
            <a href="/timestamp-converter" className="block p-4 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors">
              <h3 className="font-semibold text-gray-800 mb-1">Unix Timestamp Converter</h3>
              <p className="text-sm text-gray-600">Convert timestamps online</p>
            </a>
            <a href="/python-timestamp" className="block p-4 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 transition-colors">
              <h3 className="font-semibold text-gray-800 mb-1">Python Timestamp Guide</h3>
              <p className="text-sm text-gray-600">datetime, time modules</p>
            </a>
            <a href="/sql-timestamp" className="block p-4 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200 transition-colors">
              <h3 className="font-semibold text-gray-800 mb-1">SQL Timestamp Guide</h3>
              <p className="text-sm text-gray-600">MySQL, PostgreSQL, Oracle, SQL Server</p>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
